/**
 * @name Simulation.js
 */
import * as THREE from "three";

import ExternalForce from "./ExternalForce";
import Advection from "./Advection";
import Viscous from "./Viscous";
import Divergence from "./Divergence";
import Poisson from "./Poisson";
import Pressure from "./Pressure";

class Simulation {
    constructor(stage, params) {
        this.stage = stage;
        this.params = params;

        // バッファ
        this.fbos = {
            // 速度。わかりやすく交互に使う。
            vel_0: null,
            vel_1: null,

            // 粘性項を算出。交互に使う。
            vel_viscous0: null,
            vel_viscous1: null,

            // pressure算出のための発散の値
            div: null,

            // pressureを算出。交互に使う。
            pressure_0: null,
            pressure_1: null
        }

        // サイズ
        this.fboSize = new THREE.Vector2();
        this.cellScale = new THREE.Vector2();
        this.boundarySpace = new THREE.Vector2();

        this.__init();
    }

    /**
     * @name init
     */
    __init() {
        this.__calcSize();
        this.__createAllFBO();
        this.__createShaderPass();
    }

    /**
     * @name __calcSize
     */
    __calcSize() {
        const width = Math.round(this.params.size.w * this.params.options.resolution);
        const height = Math.round(this.params.size.h * this.params.options.resolution);

        const px_x = 1.0 / width;
        const px_y = 1.0 / height;

        this.cellScale.set(px_x, px_y);
        this.fboSize.set(width, height);
    }

    /**
     * @name __createAllFBO
     */
    __createAllFBO() {
        const type = UA.data.device == "pc" ? THREE.FloatType : THREE.HalfFloatType;
        const format = THREE.RGBAFormat;
        const mag = THREE.NearestFilter;
        const min = THREE.NearestFilter;

        for (let key in this.fbos) {
            this.fbos[key] = new THREE.WebGLRenderTarget(
                this.fboSize.x,
                this.fboSize.y,
                {
                    type: type,
                    format: format,
                }
            );
        }
    }

    /**
     * @name __createShaderPass
     */
    __createShaderPass() {

        // 移流を追加
        this.advection = new Advection(this.stage, {
            cellScale: this.cellScale,
            fboSize: this.fboSize,
            dt: this.params.options.dt,
            src: this.fbos.vel_0,
            dst: this.fbos.vel_1
        });

        // 外力を追加
        this.externalForce = new ExternalForce(this.stage, {
            cellScale: this.cellScale,
            cursorSize: this.params.options.cursorSize,
            dst: this.fbos.vel_1,
        });

        // 粘性を追加
        this.viscous = new Viscous(this.stage, {
            cellScale: this.cellScale,
            boundarySpace: this.boundarySpace,
            viscous: this.params.options.viscous,
            src: this.fbos.vel_1,
            dst: this.fbos.vel_viscous1,
            dst_: this.fbos.vel_viscous0,
            dt: this.params.options.dt,
        });

        // 発散を追加
        this.divergence = new Divergence(this.stage, {
            cellScale: this.cellScale,
            boundarySpace: this.boundarySpace,
            src: this.fbos.vel_viscous0,
            dst: this.fbos.div,
            dt: this.params.options.dt,
        });

        // ポアソンを追加
        this.poisson = new Poisson(this.stage, {
            cellScale: this.cellScale,
            boundarySpace: this.boundarySpace,
            src: this.fbos.div,
            dst: this.fbos.pressure_1,
            dst_: this.fbos.pressure_0,
        });

        // 圧力を追加
        this.pressure = new Pressure(this.stage, {
            cellScale: this.cellScale,
            boundarySpace: this.boundarySpace,
            src_p: this.fbos.pressure_0,
            src_v: this.fbos.vel_viscous0,
            dst: this.fbos.vel_0,
            dt: this.params.options.dt,
        });
    }

    /**
     * @name __raf
     */
    __raf() {

        // バウンダリの設定
        if (this.params.options.isBounce) {
            this.boundarySpace.set(0, 0);
        } else {
            this.boundarySpace.copy(this.cellScale);
        }

        // 速度の移流
        this.advection.__raf({
            dt: this.params.options.dt,
            isBounce : this.params.options.isBounce,
            BFECC: this.params.options.BFECC
        });

        // 外力の追加
        this.externalForce.__raf({
            cursorSize: this.params.options.cursorSize,
            mouseForce: this.params.options.mouseForce,
            cellScale: this.cellScale,
        });

        let vel = this.fbos.vel_1;

        // 粘性の追加
        if (this.params.options.isViscous) {
            vel = this.viscous.__raf({
                viscous: this.params.options.viscous,
                iterations: this.params.options.iterationsViscous,
                dt: this.params.options.dt
            });
        }

        // 発散の計算
        this.divergence.__raf({ vel });

        // ポアソンの計算
        const pressure = this.poisson.__raf({
            iterations: this.params.options.iterationsPoisson
        });

        // 圧力の計算
        this.pressure.__raf({ vel, pressure });
    }

    /**
     * @name __resize
     */
    __resize() {
        this.__calcSize();

        for (let key in this.fbos) {
            this.fbos[key].setSize(this.fboSize.x, this.fboSize.y);
        }
    }
}

export default Simulation;